// API Base URL - configurar según entorno
const API_BASE_URL = (window as any).ENV?.VITE_API_URL || import.meta.env?.VITE_API_URL || 'http://localhost:5000';

// Tipos de autenticación según OpenAPI
export interface LoginDto {
  emailOrUsername: string;
  password: string;
}

export interface UserDto {
  id: string;
  username: string | null;
  email: string | null;
  fullName: string | null;
  role: string | null;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface AuthResponseDto {
  accessToken: string | null;
  refreshToken: string | null;
  tokenType: string | null;
  expiresIn: number;
  user: UserDto;
}

export interface RefreshTokenDto {
  refreshToken: string;
}

// Tipos de Work Orders según OpenAPI actualizado
export interface ProductDto {
  id: string;
  name: string | null;
  externalId: string | null;
  description: string | null;
  isActive: boolean;
  isSellable: boolean;
  taxRate?: number | null;

  warehouseLink?: boolean | null;
  adjustByKilos?: boolean | null;
  family?: string | null;
  variety?: string | null;
  category?: string | null;
  tdto?: string | null;
  printLines: number;
  availablePackaging: ProductPackagingDto[];
  defaultPackagingContainerId?: string | null;
  defaultPackagingContainerName?: string | null;
}

export interface ProductPackagingDto {
  id: string;
  productId: string;
  packagingContainerId: string;
  packagingContainerName: string;
  packagingContainerCode: string;
  weightPerBoxKg?: number | null;
  isDefault: boolean;
}

export interface WorkOrderLineDto {
  id: string;
  productId: string | null; // NEW
  productName: string | null; // Computed, read-only
  packagingContainerId?: string | null;
  packagingContainerName?: string | null;
  quantity: number;
  unit: string | null;
  observations: string | null;
  allocations?: FarmerAllocationDto[];
}

export interface WorkOrderDto {
  id: string;
  customerId: string | null; // NEW
  customerName: string | null; // Computed, read-only
  orderDate: string | null;
  status: string | null;
  observations: string | null;
  routeId?: string | null;
  routeName?: string | null;
  palletTypeId?: string | null;
  palletTypeName?: string | null;
  // Snapshot de dirección de entrega (texto plano persistido en la comanda)
  deliveryCustomerAddressId?: string | null;
  deliveryAddressName?: string | null;
  deliveryAddressLine?: string | null;
  deliveryPostalCode?: string | null;
  deliveryCity?: string | null;
  deliveryProvince?: string | null;
  deliveryCountry?: string | null;
  lines: WorkOrderLineDto[] | null;
  createdAt: string;
  updatedAt: string | null;
  createdBy: {
    id: string;
    name: string;
    username: string;
  };
}

export interface CreateWorkOrderLineDto {
  productId: string; // UUID - CHANGED from productName
  packagingContainerId?: string | null;
  quantity: number;
  unit: string | null;
  observations?: string | null;
}

export interface CreateWorkOrderDto {
  customerId: string | null; // UUID - CHANGED from customerName
  orderDate: string | null;
  observations: string | null;
  routeId?: string | null;
  palletTypeId?: string | null;
  customerAddressId?: string | null;
  lines: CreateWorkOrderLineDto[] | null;
}

export interface VoiceCatalogCandidateDto {
  id: string;
  name: string;
  secondary?: string | null;
}

export interface VoiceCatalogSelectionDto {
  query: string;
  id?: string | null;
  name?: string | null;
  candidates: VoiceCatalogCandidateDto[];
}

export interface VoiceOrderDraftLineDto {
  product: VoiceCatalogSelectionDto;
  packaging?: VoiceCatalogSelectionDto | null;
  quantity: number;
  unit: string;
  observations?: string | null;
}

export interface VoiceOrderDraftDto {
  hasDraft: boolean;
  transcript: string;
  assistantMessage: string;
  customer: VoiceCatalogSelectionDto;
  orderDate: string;
  route?: VoiceCatalogSelectionDto | null;
  customerAddress?: VoiceCatalogSelectionDto | null;
  observations?: string | null;
  lines: VoiceOrderDraftLineDto[];
  warnings: string[];
  canCreate: boolean;
}

export interface UpdateWorkOrderDto {
  customerId: string | null; // UUID - CHANGED from customerName
  orderDate: string | null;
  observations: string | null;
  routeId?: string | null;
  palletTypeId?: string | null;
  customerAddressId?: string | null;
  lines: CreateWorkOrderLineDto[] | null;
}

export interface UpdateWorkOrderStatusDto {
  status: string | null;
}

// Variable para controlar si ya se está refrescando el token
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// Helper para hacer logout
function performLogout() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
}

// Helper para refrescar el token
async function refreshAccessToken(): Promise<string | null> {
  // Si ya se está refrescando, esperar a que termine
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        performLogout();
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken } as RefreshTokenDto),
      });

      if (!response.ok) {
        // Si el refresh token también expiró, hacer logout
        performLogout();
        return null;
      }

      const data: AuthResponseDto = await response.json();
      
      if (data.accessToken && data.refreshToken) {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        return data.accessToken;
      }

      performLogout();
      return null;
    } catch (error) {
      console.error('Error refreshing token:', error);
      performLogout();
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Helper para hacer fetch con retry automático al refrescar token
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  // Primera petición
  let response = await fetch(url, options);

  // Si es 401 (Unauthorized), intentar refrescar el token
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    
    if (newToken) {
      // Reintentar la petición con el nuevo token
      const headers = new Headers(options.headers);
      headers.set('Authorization', `Bearer ${newToken}`);
      
      response = await fetch(url, {
        ...options,
        headers,
      });
    }
  }

  return response;
}

// Helper para manejar respuestas
export async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

// Autenticación
export const authApi = {
  async login(credentials: LoginDto): Promise<AuthResponseDto> {
    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });
    return handleResponse<AuthResponseDto>(response);
  },

  async refreshToken(refreshToken: string): Promise<AuthResponseDto> {
    const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken } as RefreshTokenDto),
    });
    return handleResponse<AuthResponseDto>(response);
  },

  async revokeToken(refreshToken: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/auth/revoke`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken } as RefreshTokenDto),
    });
    if (!response.ok) {
      throw new Error('Failed to revoke token');
    }
  },

  async getUserById(id: string, accessToken: string): Promise<UserDto> {
    const response = await fetch(`${API_BASE_URL}/api/auth/users/${id}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });
    return handleResponse<UserDto>(response);
  },

  async updateUser(userId: string, data: UpdateUserDto): Promise<UserDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/users/${userId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<UserDto>(response);
  },

  async changePassword(userId: string, data: ChangePasswordDto): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/users/${userId}/password`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to change password');
    }
  },
};

// Helper para obtener el token actual
export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

// Helper para crear headers autenticados
export function getAuthHeaders(): HeadersInit {
  const token = getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}

// Work Orders API
export const ordersApi = {
  async getAll(params?: { status?: string; dateFrom?: string; dateTo?: string; customerIds?: string[] }): Promise<WorkOrderDto[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);
    params?.customerIds?.forEach(id => queryParams.append('customerIds', id));
    
    const url = `${API_BASE_URL}/api/orders${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<WorkOrderDto[]>(response);
  },

  async getById(id: string): Promise<WorkOrderDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<WorkOrderDto>(response);
  },

  async chatWithClaudia(data: {
    message?: string;
    audio?: Blob;
    fileName?: string;
    currentDraft?: VoiceOrderDraftDto | null;
    history?: Array<{ role: 'user' | 'assistant'; content: string }>;
  }): Promise<VoiceOrderDraftDto> {
    const form = new FormData();
    if (data.message?.trim()) form.append('message', data.message.trim());
    if (data.audio) form.append('audio', data.audio, data.fileName ?? 'claudia.webm');
    if (data.currentDraft) form.append('currentDraft', JSON.stringify(data.currentDraft));
    if (data.history?.length) form.append('history', JSON.stringify(data.history.slice(-8)));
    const token = getAccessToken();
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/voice-draft`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    return handleResponse<VoiceOrderDraftDto>(response);
  },

  async transcribeClaudia(audio: Blob, fileName: string): Promise<string> {
    const form = new FormData();
    form.append('audio', audio, fileName);
    const token = getAccessToken();
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/voice-transcription`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: form,
    });
    return (await handleResponse<{ transcript: string }>(response)).transcript;
  },

  async create(data: CreateWorkOrderDto): Promise<WorkOrderDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<WorkOrderDto>(response);
  },

  async update(id: string, data: UpdateWorkOrderDto): Promise<WorkOrderDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<WorkOrderDto>(response);
  },

  async updateStatus(id: string, status: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${id}/status`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ status } as UpdateWorkOrderStatusDto),
    });
    if (!response.ok) {
      throw new Error(`Failed to update status: ${response.statusText}`);
    }
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete order: ${response.statusText}`);
    }
  },

  async assignUser(orderId: string, userId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${orderId}/assign`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      throw new Error(`Failed to assign user: ${response.statusText}`);
    }
  },

  async getProducts(): Promise<ProductDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/products`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ProductDto[]>(response);
  },

  // Print batch: send multiple order IDs to print
  async printBatch(data: { orderIds: string[]; printerId?: string; copies?: number }): Promise<{ message: string; jobId: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/print/batch`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<any>(response);
  },

  // Print batch: print all orders for a specific date or date range.
  // Si from === to, manda 'date'; si difieren, manda 'dateFrom' + 'dateTo'.
  async printDailyBatch(date: string): Promise<{ message: string; jobId: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/print/batch?date=${date}`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<any>(response);
  },

  async printBatchByRange(from: string, to: string): Promise<{ message: string; jobId: string }> {
    const params = new URLSearchParams();
    if (from === to) {
      params.set('date', from);
    } else {
      params.set('dateFrom', from);
      params.set('dateTo', to);
    }
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/print/batch?${params.toString()}`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<any>(response);
  },

  // Print single order: optional printerId and copies as query params
  async printOrder(id: string, options?: { printerId?: string; copies?: number }): Promise<{ jobId: string; message: string; statusUrl?: string }> {
    const params = new URLSearchParams();
    if (options?.printerId) params.append('printerId', options.printerId);
    // Backend requires 'copies' parameter (defaults to 1)
    params.append('copies', String(typeof options?.copies !== 'undefined' ? options.copies : 1));
    const url = `${API_BASE_URL}/api/orders/${id}/print${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<any>(response);
  },

  async getStatistics(params?: { dateFrom?: string; dateTo?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params?.dateTo) queryParams.append('dateTo', params.dateTo);

    const url = `${API_BASE_URL}/api/orders/statistics${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<any>(response);
  },

  async palletPreview(data: {
    lines: { productId: string; packagingContainerId?: string | null; quantity: number }[];
    palletTypeId?: string;
    algorithm?: string;
  }): Promise<import('../types/loading-plan').PalletPreviewResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/pallet-preview`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<import('../types/loading-plan').PalletPreviewResponse>(response);
  },

  async getSavedPallet(orderId: string): Promise<import('../types/loading-plan').SavedPalletResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${orderId}/pallet`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<import('../types/loading-plan').SavedPalletResponse>(response);
  },

  async recalculatePallet(orderId: string, algorithm?: string): Promise<import('../types/loading-plan').SavedPalletResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${orderId}/pallet/recalculate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ algorithm: algorithm ?? null }),
    });
    return handleResponse<import('../types/loading-plan').SavedPalletResponse>(response);
  },

  async saveManualPallet(
    orderId: string,
    layout: import('../types/loading-plan').PalletPreviewResponse,
  ): Promise<import('../types/loading-plan').SavedPalletResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${orderId}/pallet`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(layout),
    });
    return handleResponse<import('../types/loading-plan').SavedPalletResponse>(response);
  },

  async manualPalletAssignment(
    orderId: string,
    request: import('../types/loading-plan').ManualAssignmentRequest,
  ): Promise<
    | import('../types/loading-plan').SavedPalletResponse
    | { errors: import('../types/loading-plan').ManualAssignmentValidationError[] }
  > {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/orders/${orderId}/pallet/manual-assignment`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });
    if (response.status === 400) {
      const body = await response.json();
      return body as { errors: import('../types/loading-plan').ManualAssignmentValidationError[] };
    }
    return handleResponse<import('../types/loading-plan').SavedPalletResponse>(response);
  },

  async getTruckLoad(routeId: string, date: string): Promise<TruckLoadView> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/truck-load?routeId=${encodeURIComponent(routeId)}&date=${encodeURIComponent(date)}`,
      { headers: getAuthHeaders() },
    );
    return handleResponse<TruckLoadView>(response);
  },

  async setTruckLoadOrdering(routeId: string, date: string, payload: SetRouteStopOrderingDto): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/truck-load/ordering?routeId=${encodeURIComponent(routeId)}&date=${encodeURIComponent(date)}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      },
    );
    if (!response.ok && response.status !== 204) {
      throw new Error(await response.text() || `Failed to save ordering: ${response.statusText}`);
    }
  },

  /**
   * Abre la vista de carga en una pestaña nueva sin pasar por la impresora.
   * Es la versión preview (sin autoprint).
   */
  async previewTruckLoad(routeId: string, date: string): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/truck-load/print-html?routeId=${encodeURIComponent(routeId)}&date=${encodeURIComponent(date)}&preview=1`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      throw new Error(await response.text() || `Failed to preview truck load: ${response.statusText}`);
    }
    const html = await response.text();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    if (!win) {
      throw new Error('El navegador bloqueó la ventana emergente. Permítela y vuelve a intentar.');
    }
  },

  /**
   * Encola la impresión de la vista de carga en la impresora configurada.
   */
  async printTruckLoad(routeId: string, date: string, copies = 1): Promise<{ jobId: string }> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/truck-load/print?routeId=${encodeURIComponent(routeId)}&date=${encodeURIComponent(date)}&copies=${copies}`,
      { method: 'POST', headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const body = await response.text();
      try {
        const json = JSON.parse(body);
        throw new Error(json.message || body);
      } catch {
        throw new Error(body || `Failed to print truck load: ${response.statusText}`);
      }
    }
    return response.json();
  },
};

// Truck load view types — direcciones agrupadas por parada para una ruta+fecha
export interface TruckLoadOrder {
  orderId: string;
  customerId: string;
  customerName: string;
  palletCount: number;
}

export interface TruckLoadStop {
  stopKey: string;
  addressName: string | null;
  addressLine: string | null;
  postalCode: string | null;
  city: string | null;
  province: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  orders: TruckLoadOrder[];
  totalPallets: number;
}

export interface TruckLoadView {
  routeId: string;
  routeName: string;
  date: string;
  stops: TruckLoadStop[];
  ordersWithoutAddress: TruckLoadOrder[];
  totalPallets: number;
  /** Dimensiones (cm) del tipo de palet más usado en la ruta. Nulo si ninguna comanda tiene palet. */
  palletLengthCm: number | null;
  palletWidthCm: number | null;
  /** Procedencia del orden de paradas: 'optimized' (OSRM), 'manual' (drag&drop), o null si nunca se guardó. */
  orderingSource: 'optimized' | 'manual' | null;
  orderingUpdatedAt: string | null;
  orderingDistanceKm: number | null;
  orderingDurationMin: number | null;
}

export interface SetRouteStopOrderingDto {
  stopKeys: string[];
  source: 'optimized' | 'manual';
  distanceKm?: number | null;
  durationMin?: number | null;
}

// Products API
export const productsApi = {
  async getAll(params?: import('../types').ProductsQueryParams): Promise<import('../types').PagedResponse<ProductDto>> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.isSellable !== undefined) queryParams.append('isSellable', params.isSellable.toString());
    if (params?.search) queryParams.append('search', params.search);
    
    const url = `${API_BASE_URL}/api/products${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<import('../types').PagedResponse<ProductDto>>(response);
  },

  async getById(id: string): Promise<ProductDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/products/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ProductDto>(response);
  },

  async create(data: CreateProductDto): Promise<ProductDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/products`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ProductDto>(response);
  },

  async update(id: string, data: UpdateProductDto): Promise<ProductDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/products/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ProductDto>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/products/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete product: ${response.statusText}`);
    }
  },

  async bulkImport(request: import('../types').BulkImportRequest<CreateProductDto>): Promise<import('../types').BulkImportResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/products/bulk-import`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });
    return handleResponse<import('../types').BulkImportResponse>(response);
  },

  async importFromExcel(file: File): Promise<import('../types').BulkImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = getAccessToken();
    const response = await fetchWithAuth(`${API_BASE_URL}/api/products/import-excel`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    return handleResponse<import('../types').BulkImportResponse>(response);
  },
};

// Customer DTOs
export interface CustomerDto {
  id: string;
  name: string | null;
  externalId: string | null;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string | null;
  updatedBy: string | null;
  
  // Contact Information
  province?: string | null;
  phone?: string | null;
  taxId?: string | null;
  contact?: string | null;
  alias?: string | null;
  email?: string | null;
  zone?: string | null;
  
  // Dates
  registrationDate?: string | null;
  licenseDate?: string | null;
  
  // Codes and References
  maintenanceCode?: string | null;
  accountCode?: string | null;
  emerging?: string | null;
  mimercaUserId?: string | null;
  mimercaEmail?: string | null;
  
  // Geographic
  country?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  
  // Banking
  bankAccount01?: string | null;
  bankAccount04?: string | null;
  ibanTransfer?: string | null;
  
  // Invoicing
  invoiceType?: string | null;
  invoiceGroup?: string | null;
  electronicInvoice?: boolean | null;
  electronicInvoiceCode?: string | null;
  series?: string | null;
  
  // Business Configuration
  isNational?: boolean | null;
  isPackagingExempt?: boolean | null;
  rate?: number | null;
  paymentMethod?: string | null;
  creditLimit?: number | null;
  discount?: number | null;
  applyDiscountToAll?: boolean | null;
  
  // Additional Fields
  quantityRangeTitle?: string | null;
  acu347?: number | null;
  publicAdminAccounting?: string | null;
  publicAdminManager?: string | null;
  publicAdminProposer?: string | null;
  publicAdminProcessor?: string | null;
  logicalOperationalPoint?: string | null;
  associatedClient?: string | null;
}

export interface CreateCustomerDto {
  name: string;
  externalId: string | null;
  isActive: boolean;
  
  // Contact Information
  province?: string | null;
  phone?: string | null;
  taxId?: string | null;
  contact?: string | null;
  alias?: string | null;
  email?: string | null;
  zone?: string | null;
  
  // Dates
  registrationDate?: string | null;
  licenseDate?: string | null;
  
  // Codes and References
  maintenanceCode?: string | null;
  accountCode?: string | null;
  emerging?: string | null;
  mimercaUserId?: string | null;
  mimercaEmail?: string | null;
  
  // Geographic
  country?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  
  // Banking
  bankAccount01?: string | null;
  bankAccount04?: string | null;
  ibanTransfer?: string | null;
  
  // Invoicing
  invoiceType?: string | null;
  invoiceGroup?: string | null;
  electronicInvoice?: boolean | null;
  electronicInvoiceCode?: string | null;
  series?: string | null;
  
  // Business Configuration
  isNational?: boolean | null;
  isPackagingExempt?: boolean | null;
  rate?: number | null;
  paymentMethod?: string | null;
  creditLimit?: number | null;
  discount?: number | null;
  applyDiscountToAll?: boolean | null;
  
  // Additional Fields
  quantityRangeTitle?: string | null;
  acu347?: number | null;
  publicAdminAccounting?: string | null;
  publicAdminManager?: string | null;
  publicAdminProposer?: string | null;
  publicAdminProcessor?: string | null;
  logicalOperationalPoint?: string | null;
  associatedClient?: string | null;
}

export interface UpdateCustomerDto {
  name: string;
  isActive: boolean;
  
  // Contact Information
  province?: string | null;
  phone?: string | null;
  taxId?: string | null;
  contact?: string | null;
  alias?: string | null;
  email?: string | null;
  zone?: string | null;
  
  // Dates
  registrationDate?: string | null;
  licenseDate?: string | null;
  
  // Codes and References
  maintenanceCode?: string | null;
  accountCode?: string | null;
  emerging?: string | null;
  mimercaUserId?: string | null;
  mimercaEmail?: string | null;
  
  // Geographic
  country?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  
  // Banking
  bankAccount01?: string | null;
  bankAccount04?: string | null;
  ibanTransfer?: string | null;
  
  // Invoicing
  invoiceType?: string | null;
  invoiceGroup?: string | null;
  electronicInvoice?: boolean | null;
  electronicInvoiceCode?: string | null;
  series?: string | null;
  
  // Business Configuration
  isNational?: boolean | null;
  isPackagingExempt?: boolean | null;
  rate?: number | null;
  paymentMethod?: string | null;
  creditLimit?: number | null;
  discount?: number | null;
  applyDiscountToAll?: boolean | null;
  
  // Additional Fields
  quantityRangeTitle?: string | null;
  acu347?: number | null;
  publicAdminAccounting?: string | null;
  publicAdminManager?: string | null;
  publicAdminProposer?: string | null;
  publicAdminProcessor?: string | null;
  logicalOperationalPoint?: string | null;
  associatedClient?: string | null;
}

// Customers API
export const customersApi = {
  async getAll(params?: import('../types').CustomersQueryParams): Promise<import('../types').PagedResponse<CustomerDto>> {
    const queryParams = new URLSearchParams();
    
    // Always include page parameter, default to 1
    queryParams.append('page', (params?.page || 1).toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.search) queryParams.append('search', params.search);
    
    const url = `${API_BASE_URL}/api/customers${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<import('../types').PagedResponse<CustomerDto>>(response);
  },

  async getById(id: string): Promise<CustomerDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<CustomerDto>(response);
  },

  async create(data: CreateCustomerDto): Promise<CustomerDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<CustomerDto>(response);
  },

  async update(id: string, data: UpdateCustomerDto): Promise<CustomerDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<CustomerDto>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete customer: ${response.statusText}`);
    }
  },

  async bulkImport(request: import('../types').BulkImportRequest<CreateCustomerDto>): Promise<import('../types').BulkImportResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/bulk-import`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });
    return handleResponse<import('../types').BulkImportResponse>(response);
  },

  async importFromExcel(file: File): Promise<import('../types').BulkImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = getAccessToken();
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/import-excel`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    return handleResponse<import('../types').BulkImportResponse>(response);
  },
};

// Production DTOs
export interface ProductionRecordDto {
  id: string;
  productId: string;
  productName: string | null;
  quantity: number;
  unit: string | null;
  producedAt: string;
  registeredBy: {
    id: string;
    name: string;
  };
  createdAt: string;
  observations: string | null;
}

export interface CreateProductionRecordDto {
  productId: string;
  quantity: number;
  unit: string;
  producedAt: string;
  observations?: string | null;
}

export interface ProductSummaryDto {
  productId: string;
  productName: string;
  totalQuantity: number;
  unit: string;
  recordCount: number;
}

export interface DailyProductionSummaryDto {
  date: string;
  products: ProductSummaryDto[];
}

// Production API
export const productionApi = {
  async getRecords(params?: {
    from?: string;
    to?: string;
    productId?: string;
  }): Promise<ProductionRecordDto[]> {
    const queryParams = new URLSearchParams();
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);
    if (params?.productId) queryParams.append('productId', params.productId);
    
    const url = queryParams.toString()
      ? `${API_BASE_URL}/api/production/records?${queryParams}`
      : `${API_BASE_URL}/api/production/records`;
    
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ProductionRecordDto[]>(response);
  },

  async getDailySummary(date: string): Promise<DailyProductionSummaryDto> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/production/daily-summary?date=${date}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return handleResponse<DailyProductionSummaryDto>(response);
  },

  async createRecord(data: CreateProductionRecordDto): Promise<ProductionRecordDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/production/records`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<ProductionRecordDto>(response);
  },

  async deleteRecord(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/production/records/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete production record: ${response.statusText}`);
    }
  },

  async transferProduction(data: {
    productId: string;
    unit: string;
    quantity: number;
    sourceDate: string;
    destinationDate: string;
  }): Promise<{ sourceRecordId: string; destinationRecordId: string; transferredQuantity: number }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/production/transfer`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<{ sourceRecordId: string; destinationRecordId: string; transferredQuantity: number }>(response);
  },

  async bulkTransferProduction(data: {
    sourceDate: string;
    destinationDate: string;
    items: Array<{
      productId: string;
      unit: string;
      quantity: number;
    }>;
  }): Promise<{ totalTransferred: number; results: Array<{ sourceRecordId: string; destinationRecordId: string; transferredQuantity: number }> }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/production/bulk-transfer`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<{ totalTransferred: number; results: Array<{ sourceRecordId: string; destinationRecordId: string; transferredQuantity: number }> }>(response);
  },
};

// DTOs para Products
export interface CreateProductDto {
  name: string | null;
  externalId?: string | null;
  description: string | null;
  isActive: boolean;
  isSellable?: boolean;
  taxRate?: number | null;

  warehouseLink?: boolean | null;
  adjustByKilos?: boolean | null;
  family?: string | null;
  variety?: string | null;
  category?: string | null;
  tdto?: string | null;
  printLines: number;
}

export interface UpdateProductDto {
  name: string | null;
  externalId?: string | null;
  description: string | null;
  isActive: boolean;
  isSellable?: boolean;
  taxRate?: number | null;

  warehouseLink?: boolean | null;
  adjustByKilos?: boolean | null;
  family?: string | null;
  variety?: string | null;
  category?: string | null;
  tdto?: string | null;
  printLines: number;
}

// DTOs para Users
export interface RegisterDto {
  username: string | null;
  email: string | null;
  password: string | null;
  fullName: string | null;
  role: string | null;
}

export interface UpdateUserDto {
  fullName: string | null;
  email: string | null;
  role: string | null;
}

export interface ChangePasswordDto {
  currentPassword: string | null;
  newPassword: string | null;
}

// DTOs para Printers
// DTOs para Printers
export interface PrinterCapabilities {
  paperSizes: {
    name: string;
    widthMm: number;
    heightMm: number;
  }[];
  orientations: string[];
  color: boolean;
  duplex: boolean;
  maxResolutionDpi?: number;
  
  // New Universal Driver capabilities
  inputBins?: string[];
  duplexModes?: string[];
  outputColors?: string[];
}

export interface PrintSettings {
  paperSize?: string;
  orientation?: string;
  color?: boolean;
  duplex?: boolean;
  resolutionDpi?: number;
  
  // New specific settings
  inputBin?: string;
  duplexMode?: string;
  outputColor?: string;
}

export interface CreatePrintJobDto {
  printerId: string;
  fileName: string;
  contentUrl: string;
  copies?: number;
  priority?: number;
  settings?: PrintSettings;
}

export interface PrintJobDto {
  id: string;
  jobId?: string; // Fallback for API compatibility
  printerId: string;
  fileName: string;
  contentUrl?: string;
  
  // Status: 9 possible states
  status: 'Pending' | 'Claimed' | 'Processing' | 'Printing' | 
          'Completed' | 'Failed' | 'Cancelled' | 'Paused' | 'Retrying';
  
  // Status message (descriptive)
  statusMessage?: string;
  
  // Print options
  copies: number;
  duplex?: boolean;
  color?: boolean;
  priority: number;
  
  // Retry tracking
  attempts: number;
  maxAttempts?: number;
  retryAttempt?: number;
  
  // Error tracking
  error?: string | null;
  lastError?: string;
  
  // Agent info
  agentId?: string;
  
  // Timestamps - detailed lifecycle tracking
  createdAt: string;
  claimedAt?: string | null;
  processingStartedAt?: string | null;
  printingStartedAt?: string | null;
  completedAt?: string | null;
  failedAt?: string | null;
  cancelledAt?: string | null;
  
  // Deprecated (use processingStartedAt instead)
  startedAt?: string | null;
}

// Multi-Agent Support
export interface AgentInfo {
  agentId: string;
  agentHostname: string;
  status: string;
  isOnline: boolean;
  lastSeen: string;
}

export interface PrinterDto {
  id: string | null;
  name: string | null;
  overallStatus: 'available' | 'unavailable' | 'error';
  availableVia: string[];
  agents: AgentInfo[];
  type: string | null;
  location: string | null;
  description: string | null;
  capabilities: PrinterCapabilities | null;
}

export interface AgentDto {
  agentId: string;
  hostname: string;
  status: 'online' | 'offline';
  lastHeartbeat: string;
  printersManaged: number;
}

// Users API
export const usersApi = {
  async getAll(): Promise<UserDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/users`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<UserDto[]>(response);
  },

  async getById(id: string): Promise<UserDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/users/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<UserDto>(response);
  },

  async create(data: RegisterDto): Promise<AuthResponseDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/register`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<AuthResponseDto>(response);
  },

  async update(id: string, data: UpdateUserDto): Promise<UserDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/users/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<UserDto>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/users/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete user: ${response.statusText}`);
    }
  },

  async activate(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/users/${id}/activate`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to activate user: ${response.statusText}`);
    }
  },

  async deactivate(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/users/${id}/deactivate`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to deactivate user: ${response.statusText}`);
    }
  },

  async changePassword(id: string, data: ChangePasswordDto): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/users/${id}/password`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`Failed to change password: ${response.statusText}`);
    }
  },
};

// Printers API
// Printing API
export const printingApi = {
  async getPrinters(): Promise<PrinterDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/printing/printers`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<PrinterDto[]>(response);
  },

  async createJob(data: CreatePrintJobDto): Promise<{ jobId: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/printing/jobs`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<{ jobId: string }>(response);
  },

  async getJobs(printerId?: string, limit: number = 50): Promise<PrintJobDto[]> {
    const params = new URLSearchParams();
    if (printerId) params.append('printerId', printerId);
    params.append('limit', String(limit));
    
    const url = `${API_BASE_URL}/api/printing/jobs${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<PrintJobDto[]>(response);
  },

  async deleteJob(jobId: string): Promise<{ message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/printing/jobs/${jobId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse<{ message: string }>(response);
  },

  async deleteAllJobs(printerId: string): Promise<{ message: string; deletedCount: number }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/printing/jobs?printerId=${printerId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse<{ message: string; deletedCount: number }>(response);
  },

  async pauseJob(jobId: string): Promise<{ message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/printing/jobs/${jobId}/pause`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<{ message: string }>(response);
  },

  async resumeJob(jobId: string): Promise<{ message: string }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/printing/jobs/${jobId}/resume`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse<{ message: string }>(response);
  }
};

// Deprecated: usage of printersApi should be migrated to printingApi.getAll
export const printersApi = {
  getAll: printingApi.getPrinters
};

// Agents API
export const agentsApi = {
  async getAll(): Promise<AgentDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/agents`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<AgentDto[]>(response);
  },

  async getStatus(agentId: string): Promise<AgentDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/agents/${agentId}/status`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<AgentDto>(response);
  }
};

// OAuth Clients DTOs
export interface OAuthClientDto {
  id: string;
  clientId: string;
  name: string;
  description?: string;
  type: 'PrintAgent' | 'Integration' | 'MobileApp' | 'Other';
  scopes: string[];
  isActive: boolean;
  createdAt: string;
  lastUsedAt?: string;
}

export interface RegisterClientRequestDto {
  name: string;
  description?: string;
  type: string;
  scopes: string[];
}

export interface RegisterClientResponseDto {
  clientId: string;
  clientSecret: string;
  name: string;
  scopes: string[];
}

export const oauthClientsApi = {
  async getAll(): Promise<OAuthClientDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/clients`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<OAuthClientDto[]>(response);
  },

  async register(data: RegisterClientRequestDto): Promise<RegisterClientResponseDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/clients/register`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<RegisterClientResponseDto>(response);
  },

  async getById(id: string): Promise<OAuthClientDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/clients/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<OAuthClientDto>(response);
  },

  async revoke(clientId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/clients/${clientId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to revoke client: ${response.statusText}`);
    }
  },
};

// Settings DTOs
export interface SettingDto {
  id: string;
  module: string;
  key: string;
  value: string;
  description?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface UpdateSettingDto {
  value: string;
  description?: string;
}

// Settings API
export const settingsApi = {
  async getSetting(module: string, key: string): Promise<SettingDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/settings/${module}/${key}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<SettingDto>(response);
  },

  async updateSetting(module: string, key: string, data: UpdateSettingDto): Promise<SettingDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/settings/${module}/${key}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<SettingDto>(response);
  },
};

// Farmer DTOs
export interface FarmerDto {
  id: string;
  name: string;
  externalId: string;
  isActive: boolean;
  createdAt: string;
}

export interface CreateFarmerDto {
  name: string;
  externalId: string;
}

export interface UpdateFarmerDto {
  name: string;
  externalId: string;
  isActive: boolean;
}

// Allocation DTOs — modelo nuevo: una allocation cuelga de (palet, línea, partida).
// La identidad del palet es su Guid estable dentro de PalletLayoutJson.
export interface FarmerAllocationDto {
  id: string;
  virtualPalletId: string;
  quantity: number;
  bultos: number;
  partida: string;
}

export interface PalletAllocationInputDto {
  workOrderLineId: string;
  partida: string;
  bultos: number;
  quantity: number;
}

export interface SetPalletAllocationsDto {
  allocations: PalletAllocationInputDto[];
}

// Farmers API
export const farmersApi = {
  async getAll(): Promise<FarmerDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/farmers`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<FarmerDto[]>(response);
  },

  async getById(id: string): Promise<FarmerDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/farmers/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<FarmerDto>(response);
  },

  async getByExternalId(externalId: string): Promise<FarmerDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/farmers/external/${externalId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<FarmerDto>(response);
  },

  async create(data: CreateFarmerDto): Promise<FarmerDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/farmers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<FarmerDto>(response);
  },

  async update(id: string, data: UpdateFarmerDto): Promise<FarmerDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/farmers/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<FarmerDto>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/farmers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete farmer: ${response.statusText}`);
    }
  },

  async bulkImport(request: import('../types').BulkImportRequest<CreateFarmerDto>): Promise<import('../types').BulkImportResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/farmers/bulk-import`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(request),
    });
    return handleResponse<import('../types').BulkImportResponse>(response);
  },
};

// Allocations API — bultos/kg por partida asignados a un palet concreto
export const allocationsApi = {
  async createManualPallet(orderId: string): Promise<{ palletId: string }> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/${orderId}/pallets`,
      { method: 'POST', headers: getAuthHeaders() },
    );
    if (!response.ok) {
      throw new Error(await response.text() || `Failed to create manual pallet: ${response.statusText}`);
    }
    return response.json();
  },

  async setPalletAllocations(orderId: string, palletId: string, data: SetPalletAllocationsDto): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/${orderId}/pallets/${palletId}/allocations`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
    );
    if (!response.ok && response.status !== 204) {
      const body = await response.text();
      throw new Error(body || `Failed to set pallet allocations: ${response.statusText}`);
    }
  },

  async closePallet(orderId: string, palletId: string): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/${orderId}/pallets/${palletId}/close`,
      { method: 'POST', headers: getAuthHeaders() },
    );
    if (!response.ok && response.status !== 204) {
      throw new Error(await response.text() || `Failed to close pallet: ${response.statusText}`);
    }
  },

  async setDeadWeight(orderId: string, palletId: string, deadWeightKg: number): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/${orderId}/pallets/${palletId}/dead-weight`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ deadWeightKg }),
      },
    );
    if (!response.ok && response.status !== 204) {
      throw new Error(await response.text() || `Failed to set dead weight: ${response.statusText}`);
    }
  },

  async reopenPallet(orderId: string, palletId: string): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/${orderId}/pallets/${palletId}/reopen`,
      { method: 'POST', headers: getAuthHeaders() },
    );
    if (!response.ok && response.status !== 204) {
      throw new Error(await response.text() || `Failed to reopen pallet: ${response.statusText}`);
    }
  },

  /**
   * Elimina un palet. Sus cajas (allocations) se liberan y vuelven a estar
   * disponibles para reasignar. Solo permitido en palets abiertos.
   */
  async deletePallet(orderId: string, palletId: string): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/${orderId}/pallets/${palletId}`,
      { method: 'DELETE', headers: getAuthHeaders() },
    );
    if (!response.ok && response.status !== 204) {
      throw new Error(await response.text() || `Failed to delete pallet: ${response.statusText}`);
    }
  },

  /**
   * Abre la etiqueta del palet en una pestaña nueva sin pasar por la impresora.
   * Útil para depurar el layout. Va con preview=1 (sin validación, sin autoprint).
   */
  async previewPalletLabel(orderId: string, palletId: string): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/${orderId}/pallets/${palletId}/label-html?preview=1`,
      { headers: getAuthHeaders() },
    );
    if (!response.ok) {
      throw new Error(await response.text() || `Failed to preview label: ${response.statusText}`);
    }
    const html = await response.text();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    // Liberar la URL cuando la ventana se cierre (o tras 60s como fallback).
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    if (!win) {
      throw new Error('El navegador bloqueó la ventana emergente. Permítela y vuelve a intentar.');
    }
  },

  async printPalletLabel(orderId: string, palletId: string, copies = 1): Promise<{ jobId: string }> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/orders/${orderId}/pallets/${palletId}/print-label?copies=${copies}`,
      { method: 'POST', headers: getAuthHeaders() },
    );
    if (!response.ok) {
      const body = await response.text();
      try {
        const json = JSON.parse(body);
        throw new Error(json.message || body);
      } catch {
        throw new Error(body || `Failed to print pallet label: ${response.statusText}`);
      }
    }
    return response.json();
  },
};


// Permissions DTOs
export interface PermissionDto {
  name: string;
  module: string;
  action: string;
  description: string;
}

export interface RoleDto {
  name: string;
  description: string;
  permissions: string[];
}

export interface UserPermissionsDto {
  userId: string;
  username: string;
  role: string;
  permissions: string[];
}

// Permissions API
export const permissionsApi = {
  async getMyPermissions(): Promise<UserPermissionsDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/permissions/me`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<UserPermissionsDto>(response);
  },

  async getAllPermissions(): Promise<PermissionDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/permissions`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<PermissionDto[]>(response);
  },

  async getAllRoles(): Promise<RoleDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/permissions/roles`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<RoleDto[]>(response);
  },

  async getRoleByName(roleName: string): Promise<RoleDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/auth/permissions/roles/${roleName}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<RoleDto>(response);
  },
};

// ============================================
// Audit API
// ============================================

// Helper para construir query params
function buildQueryParams(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  
  return searchParams.toString();
}

// Audit API
export const auditApi = {
  /**
   * Obtener logs con filtros y paginación
   */
  async getLogs(
    filters: import('../types/audit').AuditLogFilters = {}
  ): Promise<import('../types/audit').AuditLogPagedResponse> {
    const queryString = buildQueryParams(filters);
    const url = `${API_BASE_URL}/api/audit-logs${queryString ? '?' + queryString : ''}`;
    
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    
    return handleResponse<import('../types/audit').AuditLogPagedResponse>(response);
  },

  /**
   * Obtener log por ID con detalles completos
   */
  async getLogById(id: string): Promise<import('../types/audit').AuditLogDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/audit-logs/${id}`, {
      headers: getAuthHeaders(),
    });
    
    return handleResponse<import('../types/audit').AuditLogDto>(response);
  },

  /**
   * Obtener timeline de una entidad
   */
  async getEntityTimeline(
    entityType: string,
    entityId: string
  ): Promise<import('../types/audit').EntityTimelineDto> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/audit-logs/timeline/${entityType}/${entityId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    
    return handleResponse<import('../types/audit').EntityTimelineDto>(response);
  },

  /**
   * Obtener estadísticas de auditoría
   */
  async getStats(
    filters: import('../types/audit').AuditStatsFilters = {}
  ): Promise<import('../types/audit').AuditStatsDto> {
    const queryString = buildQueryParams(filters);
    const url = `${API_BASE_URL}/api/audit-logs/stats${queryString ? '?' + queryString : ''}`;
    
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    
    return handleResponse<import('../types/audit').AuditStatsDto>(response);
  },

  /**
   * Exportar logs a CSV o JSON
   */
  async exportLogs(
    filters: import('../types/audit').AuditExportFilters = {}
  ): Promise<Blob> {
    const queryString = buildQueryParams(filters);
    const url = `${API_BASE_URL}/api/audit-logs/export${queryString ? '?' + queryString : ''}`;
    
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    return response.blob();
  },

  /**
   * Helper para descargar archivo exportado
   */
  downloadExport(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
};
// Notifications API
export const notificationsApi = {
  async subscribe(token: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/notifications/subscribe`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ token }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to subscribe to notifications: ${response.statusText}`);
    }
  }
};

// Locations API
import type { CountryDto, ProvinceDto, CityDto, CustomerAddressDto, CreateCustomerAddressDto, UpdateCustomerAddressDto } from '../types/locations';

export const locationsApi = {
  async getCountries(): Promise<CountryDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/countries`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch countries: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getProvincesByCountry(countryId: string): Promise<ProvinceDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/provinces?countryId=${countryId}`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch provinces: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getCitiesByProvince(provinceId: string): Promise<CityDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/cities?provinceId=${provinceId}`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch cities: ${response.statusText}`);
    }
    
    return response.json();
  },
};

// Customer Addresses API
export const customerAddressesApi = {
  async getAll(customerId: string): Promise<CustomerAddressDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/${customerId}/addresses`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch customer addresses: ${response.statusText}`);
    }
    
    return response.json();
  },

  async getById(customerId: string, addressId: string): Promise<CustomerAddressDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/${customerId}/addresses/${addressId}`, {
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch address: ${response.statusText}`);
    }
    
    return response.json();
  },

  async create(customerId: string, data: CreateCustomerAddressDto): Promise<CustomerAddressDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/${customerId}/addresses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create address: ${response.statusText}`);
    }
    
    return response.json();
  },

  async update(customerId: string, addressId: string, data: UpdateCustomerAddressDto): Promise<CustomerAddressDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/${customerId}/addresses/${addressId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update address: ${response.statusText}`);
    }
    
    return response.json();
  },

  async delete(customerId: string, addressId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/${customerId}/addresses/${addressId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete address: ${response.statusText}`);
    }
  },

  async setDefault(customerId: string, addressId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/customers/${customerId}/addresses/${addressId}/set-default`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to set default address: ${response.statusText}`);
    }
  },
};

// ==================== LOGISTICS MODULE ====================

// Route DTOs
export interface RouteDto {
  id: string;
  name: string;
  code: string;
  description?: string | null;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface CreateRouteDto {
  name: string;
  code: string;
  description?: string | null;
}

export interface UpdateRouteDto {
  name: string;
  code: string;
  description?: string | null;
  isActive: boolean;
}

// Vehicle DTOs
export interface VehicleDto {
  id: string;
  name: string;
  licensePlate: string;
  vehicleType: string; // String representation from backend
  maxCapacityKg?: number | null;
  loadAreaLengthCm?: number | null;
  loadAreaWidthCm?: number | null;
  loadAreaHeightCm?: number | null;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface CreateVehicleDto {
  name: string;
  licensePlate: string;
  vehicleType: number; // 1 = OwnFleet, 2 = ExternalCarrier
  maxCapacityKg?: number | null;
  loadAreaLengthCm?: number | null;
  loadAreaWidthCm?: number | null;
  loadAreaHeightCm?: number | null;
}

export interface UpdateVehicleDto {
  name: string;
  licensePlate: string;
  vehicleType: number; // 1 = OwnFleet, 2 = ExternalCarrier
  maxCapacityKg?: number | null;
  loadAreaLengthCm?: number | null;
  loadAreaWidthCm?: number | null;
  loadAreaHeightCm?: number | null;
  isActive: boolean;
}

// Driver DTOs
export interface DriverDto {
  id: string;
  userId: string;
  userName: string;
  licenseNumber: string;
  licenseExpiryDate?: string | null;
  phone?: string | null;
  defaultVehicleId?: string | null;
  vehicleName?: string | null;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface CreateDriverDto {
  userId: string;
  licenseNumber: string;
  licenseExpiryDate?: string | null;
  phone?: string | null;
  defaultVehicleId?: string | null;
}

export interface UpdateDriverDto {
  licenseNumber: string;
  licenseExpiryDate?: string | null;
  phone?: string | null;
  defaultVehicleId?: string | null;
  isActive: boolean;
}

// Routes API
export const routesApi = {
  async getAll(params?: { search?: string; isActive?: boolean }): Promise<RouteDto[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    
    const url = `${API_BASE_URL}/api/logistics/routes${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<RouteDto[]>(response);
  },

  async getById(id: string): Promise<RouteDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/routes/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<RouteDto>(response);
  },

  async create(data: CreateRouteDto): Promise<RouteDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/routes`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<RouteDto>(response);
  },

  async update(id: string, data: UpdateRouteDto): Promise<RouteDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/routes/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<RouteDto>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/routes/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete route: ${response.statusText}`);
    }
  },
};

// Vehicles API
export const vehiclesApi = {
  async getAll(params?: { search?: string; isActive?: boolean }): Promise<VehicleDto[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    
    const url = `${API_BASE_URL}/api/logistics/vehicles${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VehicleDto[]>(response);
  },

  async getById(id: string): Promise<VehicleDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/vehicles/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VehicleDto>(response);
  },

  async create(data: CreateVehicleDto): Promise<VehicleDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/vehicles`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<VehicleDto>(response);
  },

  async update(id: string, data: UpdateVehicleDto): Promise<VehicleDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/vehicles/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<VehicleDto>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/vehicles/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete vehicle: ${response.statusText}`);
    }
  },
};

// Drivers API
export const driversApi = {
  async getAll(params?: { search?: string; isActive?: boolean }): Promise<DriverDto[]> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    
    const url = `${API_BASE_URL}/api/logistics/drivers${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<DriverDto[]>(response);
  },

  async getById(id: string): Promise<DriverDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/drivers/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<DriverDto>(response);
  },

  async getByUserId(userId: string): Promise<DriverDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/drivers/by-user/${userId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<DriverDto>(response);
  },

  async create(data: CreateDriverDto): Promise<DriverDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/drivers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<DriverDto>(response);
  },

  async update(id: string, data: UpdateDriverDto): Promise<DriverDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/drivers/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<DriverDto>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/drivers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete driver: ${response.statusText}`);
    }
  },
};


// Pallet Types API
export interface PalletType {
  id: string;
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  baseHeightCm?: number;
  maxHeightCm: number;
  maxWeightKg: number;
  isActive: boolean;
  model3DPath?: string;
  createdAt: string;
}

export interface CreatePalletTypeRequest {
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  baseHeightCm: number;
  maxHeightCm: number;
  maxWeightKg: number;
}

export interface UpdatePalletTypeRequest {
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  baseHeightCm: number;
  maxHeightCm: number;
  maxWeightKg: number;
}

export const palletTypesApi = {
  async getAll(): Promise<PalletType[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/pallet-types`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<PalletType[]>(response);
  },

  async create(data: CreatePalletTypeRequest): Promise<PalletType> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/pallet-types`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PalletType>(response);
  },

  async update(id: string, data: UpdatePalletTypeRequest): Promise<PalletType> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/pallet-types/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PalletType>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/pallet-types/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete pallet type: ${response.statusText}`);
    }
  },

  async uploadModel(id: string, file: File): Promise<{ path: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const token = getAccessToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await fetchWithAuth(`${API_BASE_URL}/api/logistics/pallet-types/${id}/model`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return handleResponse<{ path: string }>(response);
  },
};

// Packaging Containers API
export enum MaterialType {
  PlasticoRigido = 0,
  PlasticoLigero = 1,
  Madera = 2,
  Carton = 3,
  Poliestireno = 4,
}

export const MaterialTypeLabels: Record<MaterialType, string> = {
  [MaterialType.PlasticoRigido]: 'Plástico rígido',
  [MaterialType.PlasticoLigero]: 'Plástico ligero',
  [MaterialType.Madera]: 'Madera',
  [MaterialType.Carton]: 'Cartón',
  [MaterialType.Poliestireno]: 'Poliestireno',
};

export enum StackingFamily {
  Generica = 0,
  Mozuelo = 1,
  Cunera = 2,
  Relio = 3,
  Europea = 4,
  CartonEstandar = 5,
  Madera = 6,
}

export const StackingFamilyLabels: Record<StackingFamily, string> = {
  [StackingFamily.Generica]: 'Genérica',
  [StackingFamily.Mozuelo]: 'Mozuelo',
  [StackingFamily.Cunera]: 'Cunera',
  [StackingFamily.Relio]: 'Relio',
  [StackingFamily.Europea]: 'Europea',
  [StackingFamily.CartonEstandar]: 'Cartón estándar',
  [StackingFamily.Madera]: 'Madera',
};

export interface PackagingContainer {
  id: string;
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  maxWeightKg?: number;
  materialType: MaterialType;
  stackingFamily: StackingFamily;
  tareWeightKg?: number;
  costEur?: number;
  requiresSeparatorAbove: boolean;
  requiresSeparatorBelow: boolean;
  maxStackWeightKg?: number;
  isActive: boolean;
  model3DPath?: string;
  createdAt: string;
}

export interface CreatePackagingContainerRequest {
  name: string;
  code: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  maxWeightKg?: number;
  materialType?: MaterialType;
  stackingFamily?: StackingFamily;
  tareWeightKg?: number;
  costEur?: number;
  requiresSeparatorAbove?: boolean;
  requiresSeparatorBelow?: boolean;
  maxStackWeightKg?: number;
}

export interface UpdatePackagingContainerRequest {
  name: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  maxWeightKg?: number;
  materialType?: MaterialType;
  stackingFamily?: StackingFamily;
  tareWeightKg?: number;
  costEur?: number;
  requiresSeparatorAbove?: boolean;
  requiresSeparatorBelow?: boolean;
  maxStackWeightKg?: number;
}

export interface AssignPackagingRequest {
  weightPerBoxKg?: number;
}

export const packagingContainersApi = {
  async getAll(): Promise<PackagingContainer[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/packaging-containers`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<PackagingContainer[]>(response);
  },

  async getById(id: string): Promise<PackagingContainer> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/packaging-containers/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<PackagingContainer>(response);
  },

  async create(data: CreatePackagingContainerRequest): Promise<PackagingContainer> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/packaging-containers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PackagingContainer>(response);
  },

  async update(id: string, data: UpdatePackagingContainerRequest): Promise<PackagingContainer> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/packaging-containers/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<PackagingContainer>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/packaging-containers/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      throw new Error(`Failed to delete packaging container: ${response.statusText}`);
    }
  },

  async assignToProduct(containerId: string, productId: string, data: AssignPackagingRequest): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/packaging-containers/${containerId}/assign-to-product/${productId}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<void>(response);
  },

  async setDefaultForProduct(containerId: string, productId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/packaging-containers/${containerId}/set-default-for-product/${productId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });
    return handleResponse<void>(response);
  },

  async updateProductPackaging(containerId: string, productId: string, data: AssignPackagingRequest): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/packaging-containers/${containerId}/product-packaging/${productId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<void>(response);
  },

  async unassignFromProduct(containerId: string, productId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/packaging-containers/${containerId}/unassign-from-product/${productId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse<void>(response);
  },

  async uploadModel(id: string, file: File): Promise<{ path: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const token = getAccessToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await fetchWithAuth(`${API_BASE_URL}/api/packaging-containers/${id}/model`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return handleResponse<{ path: string }>(response);
  }
};

// ====================================================================
// Maintenance Module (Áreas, Equipos, Tareas, Recordatorios)
// ====================================================================

export type RecurrenceType = 'OneTime' | 'Interval';
export type IntervalUnit = 'Days' | 'Weeks' | 'Months' | 'Years';
export type LeadTimeUnit = 'Minutes' | 'Hours' | 'Days' | 'Weeks';
export type MaintenanceCategory = 'Other' | 'Lubrication' | 'Replacement' | 'Inspection' | 'Calibration' | 'Cleaning' | 'Legal';
export type MaintenancePriority = 'Low' | 'Normal' | 'High' | 'Critical';

// El backend serializa enums como números: estos arrays sirven para mapear ↔.
export const RECURRENCE_TYPES: RecurrenceType[] = ['OneTime', 'Interval'];
export const INTERVAL_UNITS: IntervalUnit[] = ['Days', 'Weeks', 'Months', 'Years'];
export const LEAD_TIME_UNITS: LeadTimeUnit[] = ['Minutes', 'Hours', 'Days', 'Weeks'];
export const MAINTENANCE_CATEGORIES: MaintenanceCategory[] = [
  'Other', 'Lubrication', 'Replacement', 'Inspection', 'Calibration', 'Cleaning', 'Legal'
];
export const MAINTENANCE_PRIORITIES: MaintenancePriority[] = ['Low', 'Normal', 'High', 'Critical'];

export interface MaintenanceAreaDto {
  id: string;
  name: string;
  description?: string | null;
  discordWebhookUrl?: string | null;
  isActive: boolean;
  equipmentCount: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateMaintenanceAreaDto {
  name: string;
  description?: string | null;
  discordWebhookUrl?: string | null;
  isActive: boolean;
}

export type UpdateMaintenanceAreaDto = CreateMaintenanceAreaDto;

export interface EquipmentDto {
  id: string;
  areaId: string;
  areaName: string;
  name: string;
  slug: string;
  code?: string | null;
  type?: string | null;
  location?: string | null;
  brand?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  purchaseDate?: string | null;
  notes?: string | null;
  isActive: boolean;
  activeTaskCount: number;
  nextTaskDueAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateEquipmentDto {
  areaId: string;
  name: string;
  code?: string | null;
  type?: string | null;
  location?: string | null;
  brand?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  purchaseDate?: string | null;
  notes?: string | null;
  isActive: boolean;
}

export type UpdateEquipmentDto = CreateEquipmentDto;

export interface ReminderInputDto {
  id?: string;
  leadTimeValue: number;
  leadTimeUnit: number; // enum como número
}

export interface ReminderDto {
  id: string;
  leadTimeValue: number;
  leadTimeUnit: number;
  lastSentForDueAt?: string | null;
}

export interface MaintenanceTaskDto {
  id: string;
  equipmentId: string;
  equipmentName: string;
  areaId: string;
  areaName: string;
  title: string;
  description?: string | null;
  category: number;
  priority: number;
  recurrenceType: number;
  intervalValue?: number | null;
  intervalUnit?: number | null;
  nextDueAt: string;
  lastCompletedAt?: string | null;
  isActive: boolean;
  reminders: ReminderDto[];
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateMaintenanceTaskDto {
  equipmentId: string;
  title: string;
  description?: string | null;
  category: number;
  priority: number;
  recurrenceType: number;
  intervalValue?: number | null;
  intervalUnit?: number | null;
  nextDueAt: string;
  isActive: boolean;
  reminders: ReminderInputDto[];
}

export type UpdateMaintenanceTaskDto = CreateMaintenanceTaskDto;

export interface CompleteMaintenanceTaskDto {
  completedAt?: string | null;
  notes?: string | null;
  costAmount?: number | null;
  attachmentUrl?: string | null;
}

export interface CompletionLogDto {
  id: string;
  maintenanceTaskId: string;
  completedAt: string;
  registeredAt: string;
  completedByUserId?: string | null;
  completedByUserName?: string | null;
  notes?: string | null;
  costAmount?: number | null;
  attachmentUrl?: string | null;
  nextDueAtSnapshot?: string | null;
}

export const maintenanceAreasApi = {
  async getAll(includeInactive = false): Promise<MaintenanceAreaDto[]> {
    const url = `${API_BASE_URL}/api/maintenance/areas${includeInactive ? '?includeInactive=true' : ''}`;
    const response = await fetchWithAuth(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },
  async getById(id: string): Promise<MaintenanceAreaDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/areas/${id}`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },
  async create(data: CreateMaintenanceAreaDto): Promise<MaintenanceAreaDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/areas`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  async update(id: string, data: UpdateMaintenanceAreaDto): Promise<MaintenanceAreaDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/areas/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/areas/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      const msg = await response.text();
      throw new Error(msg || `HTTP ${response.status}`);
    }
  },
  async testDiscord(id: string): Promise<{ sent: boolean }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/areas/${id}/test-discord`, {
      method: 'POST', headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
};

export const equipmentApi = {
  async search(params?: { areaId?: string; type?: string; includeInactive?: boolean; search?: string }): Promise<EquipmentDto[]> {
    const qp = new URLSearchParams();
    if (params?.areaId) qp.append('areaId', params.areaId);
    if (params?.type) qp.append('type', params.type);
    if (params?.includeInactive) qp.append('includeInactive', 'true');
    if (params?.search) qp.append('search', params.search);
    const url = qp.toString() ? `${API_BASE_URL}/api/maintenance/equipment?${qp}` : `${API_BASE_URL}/api/maintenance/equipment`;
    const response = await fetchWithAuth(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },
  async getById(id: string): Promise<EquipmentDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/equipment/${id}`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },
  async getBySlug(slug: string): Promise<EquipmentDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/equipment/by-slug/${encodeURIComponent(slug)}`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },
  async create(data: CreateEquipmentDto): Promise<EquipmentDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/equipment`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  async update(id: string, data: UpdateEquipmentDto): Promise<EquipmentDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/equipment/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/equipment/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      const msg = await response.text();
      throw new Error(msg || `HTTP ${response.status}`);
    }
  },
};

export const maintenanceTasksApi = {
  async search(params?: {
    equipmentId?: string; areaId?: string; dueBefore?: string; overdue?: boolean; includeInactive?: boolean;
  }): Promise<MaintenanceTaskDto[]> {
    const qp = new URLSearchParams();
    if (params?.equipmentId) qp.append('equipmentId', params.equipmentId);
    if (params?.areaId) qp.append('areaId', params.areaId);
    if (params?.dueBefore) qp.append('dueBefore', params.dueBefore);
    if (params?.overdue) qp.append('overdue', 'true');
    if (params?.includeInactive) qp.append('includeInactive', 'true');
    const url = qp.toString() ? `${API_BASE_URL}/api/maintenance/tasks?${qp}` : `${API_BASE_URL}/api/maintenance/tasks`;
    const response = await fetchWithAuth(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },
  async upcoming(withinDays = 30, limit = 20): Promise<MaintenanceTaskDto[]> {
    const url = `${API_BASE_URL}/api/maintenance/tasks/upcoming?withinDays=${withinDays}&limit=${limit}`;
    const response = await fetchWithAuth(url, { headers: getAuthHeaders() });
    return handleResponse(response);
  },
  async getById(id: string): Promise<MaintenanceTaskDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/tasks/${id}`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },
  async create(data: CreateMaintenanceTaskDto): Promise<MaintenanceTaskDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/tasks`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  async update(id: string, data: UpdateMaintenanceTaskDto): Promise<MaintenanceTaskDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/tasks/${id}`, {
      method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/tasks/${id}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      const msg = await response.text();
      throw new Error(msg || `HTTP ${response.status}`);
    }
  },
  async complete(id: string, data: CompleteMaintenanceTaskDto): Promise<MaintenanceTaskDto> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/tasks/${id}/complete`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  async history(id: string, take = 100): Promise<CompletionLogDto[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/tasks/${id}/history?take=${take}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },
  async deleteReminder(reminderId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/maintenance/reminders/${reminderId}`, {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      const msg = await response.text();
      throw new Error(msg || `HTTP ${response.status}`);
    }
  },
};
